import { ethSigToPrivate, getStarkKey, pedersen } from "@scure/starknet";
import { keccak256, toBytes } from "viem";

const PARADEX_SYSTEM_CONFIG_URL = "https://api.prod.paradex.trade/v1/system/config";

const CONTRACT_ADDRESS_PREFIX = BigInt("0x535441524b4e45545f434f4e54524143545f41444452455353"); // 'STARKNET_CONTRACT_ADDRESS'
const ADDR_BOUND = 2n ** 251n - 256n;

/** Normalize a hex string to a BigInt, handling both with and without 0x prefix. */
function hexToBigInt(hex: string): bigint {
  return BigInt(hex.startsWith("0x") ? hex : `0x${hex}`);
}

type ParadexSystemConfig = {
  paraclear_account_hash: string;
  paraclear_account_proxy_hash: string;
  l1_chain_id: string;
};

/**
 * Fetch Paradex system config (public API, no auth needed).
 * Returns the class hashes and chain ID needed for address derivation.
 */
export async function fetchParadexSystemConfig(): Promise<ParadexSystemConfig> {
  const response = await fetch(PARADEX_SYSTEM_CONFIG_URL);
  if (!response.ok) {
    throw new Error(`Failed to fetch Paradex system config: ${response.status}`);
  }
  const data = await response.json();
  return {
    paraclear_account_hash: data.paraclear_account_hash,
    paraclear_account_proxy_hash: data.paraclear_account_proxy_hash,
    l1_chain_id: data.l1_chain_id,
  };
}

/**
 * Build the EIP-712 typed data for "STARK Key" derivation.
 * This is the standard Paradex onboarding message that derives the StarkNet key from an EVM wallet.
 */
export function buildStarkKeyTypedData(ethereumChainId: string) {
  const chainId = Number(ethereumChainId);
  return {
    domain: {
      name: "Paradex",
      chainId,
      version: "1",
    },
    primaryType: "Constant" as const,
    types: {
      Constant: [{ name: "action", type: "string" }],
    },
    message: {
      action: "STARK Key",
    },
  };
}

const MASK_250 = 2n ** 250n - 1n;

/**
 * Compute starknetKeccak selector for a StarkNet function name, masked to 250 bits.
 * Matches starknet.js `getSelectorFromName` which uses `starknetKeccak(name) & MASK_250`.
 */
function getSelectorFromName(name: string): bigint {
  return BigInt(keccak256(toBytes(name))) & MASK_250;
}

/**
 * Compute Pedersen hash on elements array, following StarkNet's convention:
 * hash = reduce([...data, data.length], pedersen, 0)
 */
function computeHashOnElements(data: bigint[]): bigint {
  return [...data, BigInt(data.length)].reduce((acc, val) => hexToBigInt(pedersen(acc, val)), 0n);
}

export type ParadexDerivedKeys = {
  privateKey: string;
  publicKey: string;
  accountAddress: string;
};

/**
 * Derive the full set of Paradex StarkNet keys from an EVM signature.
 *
 * Chain: EVM signature → ethSigToPrivate → getStarkKey → calculateContractAddress
 *
 * @param signature - The EIP-712 "STARK Key" signature from the EVM wallet (0x-prefixed hex)
 * @param paraclearAccountHash - The account class hash from Paradex system config
 * @param paraclearAccountProxyHash - The proxy class hash from Paradex system config
 * @returns The derived private key (hex without 0x), public key (0x-prefixed hex), and account address (0x-prefixed hex)
 */
export function deriveParadexKeys(
  signature: string,
  paraclearAccountHash: string,
  paraclearAccountProxyHash: string,
): ParadexDerivedKeys {
  // 1. Derive StarkNet private key from EVM signature
  const privateKey = ethSigToPrivate(signature);

  // 2. Derive StarkNet public key from private key
  const publicKeyHex = getStarkKey(privateKey);
  const publicKey = hexToBigInt(publicKeyHex);

  // 3. Build constructor calldata
  // The Paradex account proxy's constructor takes: [accountClassHash, selector("initialize"), 2, publicKey, 0]
  const initSelector = getSelectorFromName("initialize");
  const accountClassHash = hexToBigInt(paraclearAccountHash);
  const proxyClassHash = hexToBigInt(paraclearAccountProxyHash);

  const constructorCalldata = [
    accountClassHash, // implementation class hash
    initSelector, // selector for "initialize"
    2n, // calldata length for initialize(signer, guardian)
    publicKey, // signer = public key
    0n, // guardian = 0
  ];

  // 4. Compute calldata hash
  const calldataHash = computeHashOnElements(constructorCalldata);

  // 5. Compute contract address hash
  const addressHash = computeHashOnElements([
    CONTRACT_ADDRESS_PREFIX,
    0n, // deployer address
    publicKey, // salt
    proxyClassHash, // class hash
    calldataHash, // constructor calldata hash
  ]);

  // 6. Apply address bound
  const address = addressHash % ADDR_BOUND;

  // ethSigToPrivate returns unpadded hex without 0x prefix — pad to 64 chars for the backend DTO
  const privateKeyHex = privateKey.replace(/^0x/, "").padStart(64, "0");

  return {
    privateKey: privateKeyHex,
    publicKey: publicKeyHex,
    accountAddress: `0x${address.toString(16)}`,
  };
}

/**
 * Derive only the Paradex StarkNet account address from an EVM signature.
 * Convenience wrapper around deriveParadexKeys for backward compatibility.
 */
export function deriveParadexAccountAddress(
  signature: string,
  paraclearAccountHash: string,
  paraclearAccountProxyHash: string,
): string {
  return deriveParadexKeys(signature, paraclearAccountHash, paraclearAccountProxyHash).accountAddress;
}
